import {
	EXTERIOR_TREATMENT_CATALOG,
	INTERIOR_WOOD_CATALOG,
	PANEL_DIRECTION_CATALOG,
} from "../../domain/finishes/finishCatalog";
import type { FinishPriceDeltas } from "../../domain/finishes/finishPriceDeltas";
import {
	EXTERIOR_TREATMENTS,
	type FinishConfig,
	INTERIOR_WOOD_SPECIES,
	type InteriorPanelDirection,
	PANEL_DIRECTIONS,
} from "../../domain/finishes/types";
import type { Market } from "../../domain/location/types";
import { useT } from "../../i18n";
import { formatPrice } from "../../i18n/currency";
import type { SaunaType } from "../../types/design";

interface FinishesEditorProps {
	/**
	 * A tunnbastu has no interior panel and no glespanel — its staves ARE the
	 * wall — so the direction swatches are hidden there rather than offered as
	 * a choice with no price delta, no cut-list change and no 3D effect. Wood
	 * species still applies: the barrel buys lavvirke.
	 */
	saunaType: SaunaType;
	finishes: FinishConfig;
	/** Per-option cost delta relative to the current selection, in SEK. */
	priceDeltas: FinishPriceDeltas;
	market: Market;
	onChange: (finishes: Partial<FinishConfig>) => void;
}

interface SwatchOption<TValue extends string> {
	value: TValue;
	label: string;
	description: string;
	deltaSek: number;
	/** Colour chip. Absent for choices that are not about colour. */
	swatchHex?: string;
	/** Board glyph for the direction choice — a colour chip says nothing there. */
	glyph?: InteriorPanelDirection;
}

function DirectionGlyph({ direction }: { direction: InteriorPanelDirection }) {
	// Four boards, drawn the way they will run on the wall. No <title>: the svg
	// is aria-hidden (the label's text is the radio's accessible name), so a
	// title would never be announced — it would only raise an untranslated
	// tooltip competing with the label's own.
	const lines = [0.2, 0.4, 0.6, 0.8];
	return (
		<svg
			className="finish-swatch__glyph"
			viewBox="0 0 24 24"
			aria-hidden="true"
			focusable="false"
		>
			{lines.map((offset) =>
				direction === "standing" ? (
					<line key={offset} x1={offset * 24} y1={3} x2={offset * 24} y2={21} />
				) : (
					<line key={offset} x1={3} y1={offset * 24} x2={21} y2={offset * 24} />
				),
			)}
		</svg>
	);
}

function SwatchGroup<TValue extends string>({
	name,
	legend,
	options,
	selected,
	market,
	onSelect,
}: {
	name: string;
	legend: string;
	options: Array<SwatchOption<TValue>>;
	selected: TValue;
	market: Market;
	onSelect: (value: TValue) => void;
}) {
	const t = useT();

	return (
		<fieldset className="finish-field">
			<legend>{legend}</legend>
			<div className="finish-swatch-row">
				{options.map((option) => {
					const isSelected = option.value === selected;
					// "Included" rather than "0 kr", which reads like the option is
					// free. The selected option is always 0 by construction, and an
					// unselected option that happens to cost the same says the same
					// thing.
					const deltaLabel =
						option.deltaSek === 0
							? t.finish_deltaIncluded
							: option.deltaSek > 0
								? `+${formatPrice(option.deltaSek, market)}`
								: formatPrice(option.deltaSek, market);

					return (
						<label
							key={option.value}
							className={`finish-swatch${isSelected ? " active" : ""}`}
							title={option.description}
						>
							<input
								type="radio"
								name={name}
								value={option.value}
								checked={isSelected}
								onChange={() => onSelect(option.value)}
							/>
							{option.glyph ? (
								<DirectionGlyph direction={option.glyph} />
							) : (
								<span
									className="finish-swatch__chip"
									style={{ background: option.swatchHex }}
									aria-hidden="true"
								/>
							)}
							<span className="finish-swatch__label">{option.label}</span>
							{/* Deliberately plain text, no aria-label: the radio's
							    accessible name is the whole label's text content, so
							    "Asp, +8 000 kr" is already what a screen reader
							    announces — an aria-label here would replace that name
							    with the annotation alone. */}
							<span
								className={`finish-swatch__delta${
									!isSelected && option.deltaSek < 0 ? " down" : ""
								}`}
							>
								{deltaLabel}
							</span>
						</label>
					);
				})}
			</div>
			<p className="finish-field__hint">
				{options.find((option) => option.value === selected)?.description}
			</p>
		</fieldset>
	);
}

/**
 * The "Character" section — three aesthetic choices, as swatches rather than
 * dropdowns.
 *
 * Every option carries what it costs relative to the current one, computed
 * from the real BOM by `calculateFinishPriceDeltas`. That is the whole point
 * of the section: two people with the same footprint should be able to see,
 * before they commit, that their taste is a real number.
 */
export function FinishesEditor({
	saunaType,
	finishes,
	priceDeltas,
	market,
	onChange,
}: FinishesEditorProps) {
	const t = useT();

	return (
		<div className="inspector-section-body finish-panel">
			<p className="finish-panel__intro">{t.finish_sectionIntro}</p>

			<SwatchGroup
				name="finish-interior-wood"
				legend={t.finish_woodGroupLabel}
				selected={finishes.interiorWood}
				market={market}
				onSelect={(interiorWood) => onChange({ interiorWood })}
				options={INTERIOR_WOOD_SPECIES.map((species) => {
					const spec = INTERIOR_WOOD_CATALOG[species];
					return {
						value: species,
						label: t[spec.labelKey],
						description: t[spec.descriptionKey],
						deltaSek: priceDeltas.interiorWood[species],
						swatchHex: spec.swatchHex,
					};
				})}
			/>

			{saunaType !== "tunna" && (
				<SwatchGroup
					name="finish-panel-direction"
					legend={t.finish_directionGroupLabel}
					selected={finishes.panelDirection}
					market={market}
					onSelect={(panelDirection) => onChange({ panelDirection })}
					options={PANEL_DIRECTIONS.map((direction) => {
						const spec = PANEL_DIRECTION_CATALOG[direction];
						return {
							value: direction,
							label: t[spec.labelKey],
							description: t[spec.descriptionKey],
							deltaSek: priceDeltas.panelDirection[direction],
							glyph: direction,
						};
					})}
				/>
			)}

			{/* Indoors (issue 156) there is no facade to treat. */}
			{saunaType !== "inomhus" && (
				<SwatchGroup
					name="finish-exterior-treatment"
					legend={t.finish_treatmentGroupLabel}
					selected={finishes.exteriorTreatment}
					market={market}
					onSelect={(exteriorTreatment) => onChange({ exteriorTreatment })}
					options={EXTERIOR_TREATMENTS.map((treatment) => {
						const spec = EXTERIOR_TREATMENT_CATALOG[treatment];
						return {
							value: treatment,
							label: t[spec.labelKey],
							description: t[spec.descriptionKey],
							deltaSek: priceDeltas.exteriorTreatment[treatment],
							swatchHex: spec.swatchHex,
						};
					})}
				/>
			)}
		</div>
	);
}
